import { supabase, supabaseEndpoint } from '../client';

export interface SendSmsInput {
  templateKey: string;
  /** Recipient phone in E.164 format (e.g. +15551234567). */
  to: string;
  variables?: Record<string, unknown>;
  /**
   * Workspace context the send is billed and authorized against. The
   * `send-sms` Edge Function forwards these to `core.rpc_emit_sms`,
   * which resolves the Sendblue connection (store-scoped > org-scoped
   * > platform) and enforces the segment cap at the same scope.
   */
  orgId?: string | null;
  storeId?: string | null;
  /**
   * Render + queue + dispatch through the edge function in dry-run mode
   * (does not contact Sendblue). Useful for previewing operator-visible
   * copy and exercising the auth / cap path in tests.
   */
  dryRun?: boolean;
}

export interface SendSmsResult {
  ok: boolean;
  message_id?: string;
  provider_message_id?: string;
  message_kind?: string;
  error?: string;
}

/**
 * Invokes the `send-sms` Edge Function via a plain `fetch`.
 *
 * Mirrors `sendEmail` in this directory:
 *
 *   1. Same justification for bypassing `supabase.functions.invoke()` --
 *      stable CORS surface across SDK upgrades, and the raw `Response`
 *      so transactional callers (sendProposal etc.) can surface the real
 *      error from the function body.
 *   2. Always returns `{ ok, error? }` rather than throwing so the order
 *      state transition that triggered the send is never rolled back by
 *      a Sendblue / opt-out / cap-exceeded failure.
 *
 * The function does:
 *   1. Calls `core.rpc_emit_sms` under the caller's JWT (org/store
 *      access + recipient opt-out + monthly cap all enforced in SQL).
 *   2. Dispatches the queued row through the service-role
 *      `core.rpc_sms_dispatch_load` chain so the Sendblue secret never
 *      crosses the user-JWT boundary.
 */
export async function sendSms(input: SendSmsInput): Promise<SendSmsResult> {
  const sb = supabase();

  const { data: sess } = await sb.auth.getSession();
  const accessToken = sess.session?.access_token;
  if (!accessToken) {
    return {
      ok: false,
      error: 'Your session has expired. Please sign in again and retry.',
    };
  }

  const { url: baseUrl, anonKey } = supabaseEndpoint();
  const url = `${baseUrl.replace(/\/$/, '')}/functions/v1/send-sms`;

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: anonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        template_key: input.templateKey,
        to: input.to,
        variables: input.variables ?? {},
        org_id: input.orgId ?? undefined,
        store_id: input.storeId ?? undefined,
        dry_run: input.dryRun ?? false,
      }),
    });
  } catch (err) {
    const name = err instanceof Error ? err.name : 'Error';
    const msg = err instanceof Error ? err.message : String(err);
    const cause = err instanceof Error && err.cause !== undefined ? String(err.cause) : null;
    return {
      ok: false,
      error: cause
        ? `Network failure invoking send-sms (${name}): ${msg} | cause: ${cause}`
        : `Network failure invoking send-sms (${name}): ${msg}`,
    };
  }

  let body: SendSmsResult | null = null;
  let raw: string | null = null;
  try {
    raw = await resp.text();
    if (raw) body = JSON.parse(raw) as SendSmsResult;
  } catch {
    // body wasn't JSON; raw still holds the text payload (if any)
  }

  if (!resp.ok) {
    const detail = body?.error ?? raw ?? `HTTP ${resp.status}`;
    return { ok: false, error: detail };
  }

  return body ?? { ok: false, error: 'send-sms returned an empty body.' };
}

/**
 * Best-effort E.164 normalization for client-typed phone numbers.
 *
 * Accepts:
 *   * Already-formatted E.164 (`+15551234567`) -- returned as-is.
 *   * 10-digit US number (`5551234567` or `(555) 123-4567`) -- prepends `+1`.
 *   * 11-digit US number starting with `1` (`15551234567`) -- prepends `+`.
 *
 * Anything else returns `null` so callers can surface a clear validation
 * error instead of letting `rpc_emit_sms` reject the value with a generic
 * "Recipient phone must be E.164" message.
 */
export function toE164(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  if (/^\+[1-9]\d{1,14}$/.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D+/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/** Strip a phone string to digits-only for fuzzy equality checks. */
export function phoneDigitsOnly(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const digits = String(raw).replace(/\D+/g, '');
  return digits.length > 0 ? digits : null;
}
